/**
 *  @class
 *  @function CartShippingRate
 *
 *  Shows the cheapest shipping rate for a fixed country / zip (theme settings)
 *  under the cart drawer total. Uses the same prepare / async shipping rates
 *  endpoints as the shipping calculator. Hidden until a rate is known.
 */

if (!customElements.get('cart-shipping-rate')) {
  const rateCache = new Map(); // "country|zip|cartTotal" -> cheapest rate in cents (or null)

  class CartShippingRate extends HTMLElement {
    connectedCallback() {
      this.load();
    }

    async load() {
      const { country, zip, cartTotal } = this.dataset;
      const cacheKey = `${country}|${zip}|${cartTotal}`;
      try {
        if (!rateCache.has(cacheKey)) {
          rateCache.set(cacheKey, await this.fetchCheapestRate(country, zip));
        }
        this.render(rateCache.get(cacheKey));
      } catch (error) {
        this.hidden = true;
      }
    }

    async fetchCheapestRate(country, zip) {
      const query = `shipping_address[zip]=${encodeURIComponent(zip)}&shipping_address[country]=${encodeURIComponent(country)}`;
      const prepare = await fetch(`/cart/prepare_shipping_rates.json?${query}`, { method: 'POST' });
      if (!prepare.ok) throw new Error('prepare_shipping_rates failed');

      let rates = null;
      for (let attempt = 0; attempt < 6 && rates === null; attempt++) {
        const response = await fetch(`/cart/async_shipping_rates.json?${query}`);
        if (response.status === 200) {
          const data = await response.json();
          if (Array.isArray(data.shipping_rates)) rates = data.shipping_rates;
        }
        if (rates === null) await new Promise((resolve) => setTimeout(resolve, 400));
      }
      if (!rates || !rates.length) return null;

      return rates.reduce((min, rate) => Math.min(min, Math.round(parseFloat(rate.price) * 100)), Infinity);
    }

    render(cents) {
      const value = this.querySelector('[data-shipping-rate-value]');
      if (!value || cents === null || cents === undefined || !Number.isFinite(cents)) {
        this.hidden = true;
        return;
      }
      if (cents === 0) {
        value.textContent = this.dataset.freeLabel || 'Offerte';
      } else {
        const currencyFormat = window.theme.settings.money_with_currency_format || "${{amount}}";
        value.textContent = formatMoney(cents, currencyFormat);
      }
      this.hidden = false;
    }
  }

  customElements.define('cart-shipping-rate', CartShippingRate);
}
